import { useInfiniteFetchMemberships } from "containers/folders/hooks/merchants";
import { useMemo } from "react";
import { InfiniteQueryConfig } from "react-query";

const fetchMoreDelayed = (
    canFetchMore: boolean,
    isFetchingMore: boolean | string,
    isFetching: boolean,
    fetchMore: Function
) => {
    if (canFetchMore && !isFetchingMore && !isFetching) {
        process.nextTick(() => fetchMore());
    }
};

const flattenInfiniteData = <TResponse, TItem>(
    responses: TResponse[] = [],
    extractFn: (response: TResponse) => TItem[]
): TItem[] => {
    return responses.reduce((flat, toFlatten) => {
        return flat.concat(extractFn(toFlatten));
    }, []);
};

export const useFetchAllMemberships = (
    userId: string,
    options?: Partial<InfiniteQueryConfig<nuorder.merchant.v1.ListMembersResponse, Error>>
) => {
    const { data, isFetching, canFetchMore, isFetchingMore, fetchMore } = useInfiniteFetchMemberships(
        userId,
        { page_size: 30 },
        options
    );

    useMemo(
        () => fetchMoreDelayed(canFetchMore, isFetchingMore, isFetching, fetchMore),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [canFetchMore, isFetchingMore, isFetching, fetchMore, data]
    );

    const memberships = useMemo(
        () =>
            flattenInfiniteData(data, (response) => response.members).sort((a, b) => {
                // Based on this NU-8778 (after beeing reopened). Looks like BE use the following sorting. Before I used localeCompare
                const nameA = (a.merchant_ref?.name ?? a.merchant_id).toLowerCase();
                const nameB = (b.merchant_ref?.name ?? b.merchant_id).toLowerCase();
                return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
            }),
        [data]
    );
    return {
        memberships,
        isFetching,
        canFetchMore,
    };
};
